import React, { useState } from 'react';
import SelectDrawer from '../generics/select-drawer';
import { Button } from './button';

interface TimePickerDrawerProps {
  id?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  min?: string;
  max?: string;
  step?: number;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  icon?: React.ReactNode;
  // Prop pour filtrer les heures en fonction d'une heure minimum
  minTime?: string;
  // Nouvelles props pour le bouton
  withButton?: boolean;
  buttonIcon?: React.ReactNode;
  onButtonClick?: () => void;
  buttonClassName?: string;
  buttonAriaLabel?: string;
}

const TimePickerDrawer: React.FC<TimePickerDrawerProps> = ({
  id,
  name,
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  error,
  className = '',
  icon,
  minTime = '',
  // Props du bouton avec valeurs par défaut
  withButton = false,
  buttonIcon = null,
  onButtonClick,
  buttonClassName = '',
  buttonAriaLabel = 'Ajouter',
}) => {
  // Générer les options de temps par créneaux de 15 minutes, avec filtrage par heure minimale
  const generateTimeOptions = () => {
    const options = [];
    
    // Convertir minTime en minutes pour faciliter la comparaison
    let minMinutes = 0;
    if (minTime) {
      const [minHours, minMins] = minTime.split(':').map(Number);
      minMinutes = minHours * 60 + minMins;
    }
    
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const currentTimeInMinutes = hour * 60 + minute;
        
        // Ne pas inclure les heures avant minTime
        if (minTime && currentTimeInMinutes <= minMinutes) {
          continue;
        }
        
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const timeValue = `${hourStr}:${minuteStr}`;
        
        options.push({
          value: timeValue,
          label: timeValue
        });
      }
    }
    
    return options;
  };

  const timeOptions = generateTimeOptions();

  const [validationError, setValidationError] = useState<string | undefined>();

  // Formater et valider une heure saisie manuellement
  const formatAndValidateTime = (inputValue: string): { value: string; isValid: boolean } => {
    // Retirer tous les caractères non numériques et les ':'
    const cleanInput = inputValue.replace(/[^0-9:]/g, '');
    
    // Cas où l'input est vide
    if (!cleanInput) {
      return { value: '', isValid: true };
    }

    // Si l'input contient déjà un ':', traiter comme format hh:mm
    if (cleanInput.includes(':')) {
      const [hours, minutes] = cleanInput.split(':');
      const hoursNum = parseInt(hours, 10);
      const minutesNum = parseInt(minutes || '0', 10);
      
      // Valider les heures et minutes
      if (hoursNum >= 0 && hoursNum <= 23 && minutesNum >= 0 && minutesNum <= 59) {
        const formattedHours = hoursNum.toString().padStart(2, '0');
        const formattedMinutes = minutesNum.toString().padStart(2, '0');
        return { value: `${formattedHours}:${formattedMinutes}`, isValid: true };
      } else {
        return { value: cleanInput, isValid: false };
      }
    } 
    // Traiter les saisies comme 2, 02, 14, etc.
    else if (cleanInput.length <= 2) {
      const hoursNum = parseInt(cleanInput, 10);
      if (hoursNum >= 0 && hoursNum <= 23) {
        const formattedHours = hoursNum.toString().padStart(2, '0');
        return { value: `${formattedHours}:00`, isValid: true };
      } else {
        return { value: cleanInput, isValid: false };
      }
    } 
    // Traiter les saisies comme 230, 1430, 0230, etc.
    else {
      // Si l'utilisateur a entré 3 ou 4 chiffres, interpréter comme HHMM
      if (cleanInput.length === 3) {
        // Format '230' => '02:30'
        const hours = cleanInput.substring(0, 1);
        const minutes = cleanInput.substring(1);
        const hoursNum = parseInt(hours, 10);
        const minutesNum = parseInt(minutes, 10);
        
        if (hoursNum >= 0 && hoursNum <= 23 && minutesNum >= 0 && minutesNum <= 59) {
          const formattedHours = hoursNum.toString().padStart(2, '0');
          const formattedMinutes = minutesNum.toString().padStart(2, '0');
          return { value: `${formattedHours}:${formattedMinutes}`, isValid: true };
        } else {
          return { value: cleanInput, isValid: false };
        }
      } else if (cleanInput.length >= 4) {
        // Format '0230' ou '1430' => '02:30' ou '14:30'
        const hours = cleanInput.substring(0, 2);
        const minutes = cleanInput.substring(2, 4);
        const hoursNum = parseInt(hours, 10);
        const minutesNum = parseInt(minutes, 10);
        
        if (hoursNum >= 0 && hoursNum <= 23 && minutesNum >= 0 && minutesNum <= 59) {
          return { value: `${hours}:${minutes}`, isValid: true };
        } else {
          return { value: cleanInput, isValid: false };
        }
      } else {
        return { value: cleanInput, isValid: false };
      }
    }
  };

  // Gestionnaire de changement - transmet simplement la valeur pendant la saisie
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const rawValue = e.target.value;
    
    // Si c'est une sélection dans la liste (pas une saisie manuelle)
    if ((e.target as HTMLSelectElement).options) {
      setValidationError(undefined); // Réinitialiser l'erreur
      const syntheticEvent = {
        target: { 
          name: e.target.name, 
          value: rawValue 
        }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
      return;
    }
    
    // Si c'est une saisie manuelle, on passe simplement la valeur brute
    // sans validation ni formatage pendant la frappe
    const syntheticEvent = {
      target: { 
        name: e.target.name, 
        value: rawValue 
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(syntheticEvent);
  };
  
  // Gestionnaire pour l'événement blur (quand l'utilisateur quitte le champ)
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    
    // Formatter et valider la valeur quand l'utilisateur quitte le champ
    const { value: formattedValue, isValid } = formatAndValidateTime(rawValue);
    
    // Mettre à jour l'erreur de validation si nécessaire
    setValidationError(isValid ? undefined : 'Heure invalide');
    
    // Envoyer l'événement avec la valeur formatée
    const syntheticEvent = {
      target: { 
        name: e.target.name, 
        value: formattedValue 
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(syntheticEvent);
  };

  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={id || name} 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="flex gap-2 items-start">
        <div className={`flex-1 ${label ? 'mt-6' : ''}`}>
          <SelectDrawer
            id={id}
            name={name}
            value={value}
            onChange={handleSelectChange}
            onBlur={handleBlur} // Validation au blur
            options={timeOptions}
            required={required}
            disabled={disabled}
            error={validationError || error}
            className={className}
            icon={icon}
            allowCustomInput={true} // Activer la saisie manuelle
            placeholder="Choisir ou saisir une heure"
          />
        </div>
        {withButton && onButtonClick && (
          <Button
            onClick={onButtonClick}
            type="button"
            className={`${!label ? 'mt-6' : 'mt-0'} ${buttonClassName}`}
            aria-label={buttonAriaLabel}
            disabled={disabled}
          >
            {buttonIcon}
          </Button>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-500">{error}</p>
      )}
    </div>
  );
};

export default TimePickerDrawer;
